import { makeAutoObservable, runInAction } from 'mobx';
import { getIMQSubscriberById, getIMQRequiredSubInfo } from 'api/operations/electionService';
import { MedicalCategoryDelegate } from './medicalCategoryDelegate';
import { CategoryStatus, FamilyCensus, RequiredSubInfoWithIMQ, SubscriberIMQ } from './types';

interface SubscriberIMQDomainData {
  subscriberId: string;
}

/** This module gathers all the data regarding employee's health questionnaire & employment details. */
export class SubscriberImqModule {
  private _domainData: SubscriberIMQDomainData;
  private _subscriber: SubscriberIMQ | null = null;
  private _requiredSubInfo: RequiredSubInfoWithIMQ | null = null;
  private _medicalCategories: MedicalCategoryDelegate[] = [];
  private _familyCensus: FamilyCensus = {};

  private _buildFamilyCensus(): void {
    if (!this._subscriber) {
      throw new Error('Cannot build family census: Subscriber has not been loaded!');
    }

    const dependentsCensus = this._subscriber.custom.dependents.reduce((acc, dependent) => {
      let type: 'PRIMARY' | 'SPOUSE' | 'DEPENDENT';

      switch (dependent.type) {
        case 'S':
          type = 'SPOUSE';
          break;
        case 'D':
          type = 'DEPENDENT';
          break;
        default:
          type = 'PRIMARY';
          break;
      }

      acc[dependent.id] = {
        type,
        name: `${dependent.firstName} ${dependent.lastName}`,
      };
      return acc;
    }, {} as FamilyCensus);

    this._familyCensus = {
      [this._subscriber?.id]: {
        type: 'PRIMARY',
        name: `${this._subscriber?.common.firstName} ${this._subscriber?.common.lastName}`,
      },
      ...dependentsCensus,
    };
  }

  get subscriber(): SubscriberIMQ | null {
    return this._subscriber;
  }

  get familyCensus(): FamilyCensus {
    return this._familyCensus;
  }

  get requiredSubInfo(): RequiredSubInfoWithIMQ | null {
    return this._requiredSubInfo;
  }

  get questionnaireWasStarted(): boolean {
    return this._subscriber?.imqAnswers?.categories ? this._subscriber?.imqAnswers?.categories.length > 0 : false;
  }

  get medicalCategories(): MedicalCategoryDelegate[] {
    return this._medicalCategories;
  }

  get completedCategoriesCount(): number {
    return this._medicalCategories.filter((category) => category.status === CategoryStatus.COMPLETED).length;
  }

  get totalCategoriesCount(): number {
    return this._medicalCategories.length;
  }

  get categoriesProgressText(): string {
    return `${this.completedCategoriesCount}/${this.totalCategoriesCount} categories completed`;
  }

  public async getSubscriber(): Promise<void> {
    const subscriber = await getIMQSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this._subscriber = subscriber;
      this._buildFamilyCensus();
    });
  }

  public async getRequiredSubInfo(): Promise<void> {
    const requiredSubInfo = await getIMQRequiredSubInfo();
    runInAction(() => {
      this._requiredSubInfo = requiredSubInfo;
    });
  }

  public buildImqData(): void {
    if (!this.requiredSubInfo || !this._subscriber?.imqAnswers) {
      throw new Error('Cannot build IMQ data: Either subscriber or IMQ answers have not been loaded!');
    }
    this._medicalCategories = this.requiredSubInfo.imq.categories.map(
      (category) =>
        new MedicalCategoryDelegate({
          name: category.name,
          reportedConditions:
            this._subscriber?.imqAnswers?.categories.find((answer) => answer.name === category.name)
              ?.medicalConditions ?? null,
          familyCensus: this._familyCensus,
        }),
    );
  }

  constructor(domainData: SubscriberIMQDomainData) {
    this._domainData = domainData;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
